import React, { useEffect, useMemo, useState } from 'react';
import { useFetch } from '../hooks/useFetchAuth';
import MappingFields, { ProcessingSwitch } from './MappingFields';
import SampleTable from './SampleTable';
import {
  buildSheetsOverride, draftChanged, droppedSheets, initialDraft, sheetStatus, validateDraft,
} from '../utils/reprocessMapping';
import { filterNote, sampleValues } from '../utils/sheetMapping';

const STATUS_BADGES = {
  loaded: { label: 'chargé', className: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  broken: { label: 'à corriger', className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  unloaded: { label: 'non chargé', className: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  enabled: { label: 'à charger', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  disabled: { label: 'ignoré', className: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' },
};

const formatCell = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

function Badge({ status }) {
  const badge = STATUS_BADGES[status];
  return <span className={`ml-2 shrink-0 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium ${badge.className}`}>{badge.label}</span>;
}

function DataTable({ columns, rows, highlight = [] }) {
  if (!rows || rows.length === 0) return <p className="text-sm text-gray-500 dark:text-gray-400">Aucune ligne.</p>;
  return (
    <div className="overflow-auto rounded border border-gray-200 dark:border-gray-700 max-h-64">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
          <tr>
            {columns.map((c) => (
              <th key={c} className={`whitespace-nowrap px-2 py-1.5 text-left font-semibold ${highlight.includes(c) ? 'text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-gray-300'}`}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c} className="whitespace-nowrap px-2 py-1 text-gray-700 dark:text-gray-300 max-w-[16rem] truncate" title={formatCell(row[c])}>{formatCell(row[c])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Samples({ preview, column, field, load }) {
  const values = sampleValues(preview.sample_rows, column).join(' · ');
  const note = filterNote(field, load);
  if (!note) return values;
  return (
    <>
      <span className="font-medium text-gray-700 dark:text-gray-200">{note}</span>
      {values && <span> (fichier : {values})</span>}
    </>
  );
}

function MappingView({ preview, load }) {
  const entries = Object.entries(preview.mapping || {});
  return (
    <div className="flex flex-col gap-4">
      <div>
        <h4 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-100">Mapping utilisé</h4>
        {entries.length === 0 ? (
          <p className="text-sm text-red-600 dark:text-red-400">Aucun mapping enregistré pour cette feuille.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                <th className="py-1 pr-4 font-medium">Champ</th>
                <th className="py-1 pr-4 font-medium">Colonne du fichier</th>
                <th className="py-1 font-medium">Exemples</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {entries.map(([column, field]) => {
                const missing = preview.missing_columns.includes(column);
                return (
                  <tr key={column}>
                    <td className="py-1 pr-4 font-mono text-xs text-gray-800 dark:text-gray-100">{field}</td>
                    <td className={`py-1 pr-4 ${missing ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
                      {column}{missing && <span className="ml-2 text-xs">(introuvable dans le fichier)</span>}
                    </td>
                    <td className="py-1 text-xs text-gray-500 dark:text-gray-400">{missing ? '' : <Samples preview={preview} column={column} field={field} load={load} />}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {preview.error && (
        <p className="rounded bg-red-50 p-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">Le traitement échoue : {preview.error}</p>
      )}
      {preview.mapped_rows && (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
            Aperçu des données traitées
            <span className="ml-2 font-normal text-gray-500 dark:text-gray-400">
              {preview.mapped_rows.length} premières lignes sur {preview.mapped_total_rows.toLocaleString('fr-FR')}
            </span>
          </h4>
          <DataTable columns={preview.mapped_columns} rows={preview.mapped_rows} highlight={Object.values(preview.mapping || {})} />
        </div>
      )}
      {/* Nothing could be mapped: show the file itself, to see what the mapping should be */}
      {!preview.mapped_rows && (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
            Lignes du fichier
            <span className="ml-2 font-normal text-gray-500 dark:text-gray-400">
              {preview.total_rows.toLocaleString('fr-FR')} lignes dans le fichier
            </span>
          </h4>
          <SampleTable columns={preview.columns} rows={preview.sample_rows} />
        </div>
      )}
    </div>
  );
}

function RawView({ preview }) {
  return (
    <div>
      <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
        Cette feuille n'est pas chargée ({preview.total_rows.toLocaleString('fr-FR')} lignes dans le fichier). Pour la charger,
        cliquez sur « Modifier le mapping ».
      </p>
      <SampleTable columns={preview.columns} rows={preview.sample_rows} />
    </div>
  );
}

// Same layout as the upload screen: the file, the "Traiter cette feuille" switch, then the mapping
function MappingEditor({ preview, load, draft, choices, onChange }) {
  const sheetDraft = draft[preview.sheet];

  const setColumn = (field, column) => {
    const mapping = Object.fromEntries(Object.entries(sheetDraft.mapping).filter(([, f]) => f !== field));
    if (column) {
      delete mapping[column];
      mapping[column] = field;
    }
    onChange({ ...sheetDraft, mapping });
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <SampleTable columns={preview.columns} rows={preview.sample_rows} />
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Total des lignes: {preview.total_rows}</p>
      </div>
      <ProcessingSwitch checked={sheetDraft.enabled} onChange={() => onChange({ ...sheetDraft, enabled: !sheetDraft.enabled })} />
      <div className="relative">
        {!sheetDraft.enabled && (
          <div className="absolute inset-0 bg-gray-200/50 dark:bg-gray-700/50 backdrop-blur-[1px] z-10 rounded-lg" />
        )}
        <div className={!sheetDraft.enabled ? 'pointer-events-none opacity-50' : ''}>
          <MappingFields
            choices={choices}
            columns={preview.columns}
            mapping={sheetDraft.mapping}
            onChange={setColumn}
            disabled={!sheetDraft.enabled}
            sampleRows={preview.sample_rows}
            period={load}
          />
        </div>
      </div>
    </div>
  );
}

function ReprocessDialog({ item, onCancel, onConfirm }) {
  const { data, isLoading, error } = useFetch(['reprocess-preview', item.uuid], `/api/history/${item.uuid}/reprocess-preview`, {
    staleTime: 0, gcTime: 0, retry: false,
  });
  const { data: choicesData } = useFetch(
    ['mappingChoices', item.shop_sign, item.client],
    `/api/mapping-choices/${item.shop_sign}/${item.client}`,
  );
  const choices = useMemo(
    () => (choicesData?.choices || []).filter((c) => !['year', 'week'].includes(c.value)),
    [choicesData],
  );

  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});

  useEffect(() => {
    if (!data?.sheets) return;
    setDraft(initialDraft(data.sheets));
    setSelected((current) => current || (data.sheets.find((s) => s.loaded) || data.sheets[0])?.sheet);
  }, [data]);

  const sheets = data?.sheets || [];
  const preview = sheets.find((s) => s.sheet === selected);
  const brokenSheets = sheets.filter((s) => sheetStatus(s) === 'broken');
  const errors = editing ? validateDraft(draft, choices) : [];
  const changed = editing && draftChanged(draft, sheets);
  const dropped = editing ? droppedSheets(draft, sheets) : [];

  const load = data?.load;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black opacity-50" onClick={onCancel} />
      {/* Fixed height: ticking a sheet or switching modes would otherwise resize the dialog and move its buttons.
          max-w-7xl and a narrow sheet list leave the mapping fields about the width they have on the upload screen */}
      <div className="relative flex h-[90vh] w-full max-w-7xl flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800">
        <div className="border-b border-gray-200 p-5 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">{editing ? 'Modifier le mapping et retraiter' : 'Retraiter'}</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
            {item.client} · {item.shop_sign} · {item.category || 'default'} · S{String(item.week).padStart(2, '0')} {item.year} · {item.filename}
            {load?.mapping_name && <span className="ml-2 text-gray-400">(mapping {load.mapping_name})</span>}
          </p>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            {!editing && "Cela va supprimer l'ancienne ingestion et relancer le traitement avec le même mapping."}
            {editing && (changed
              ? "Le mapping modifié sera enregistré comme un nouveau mapping : l'ancien reste inchangé pour les autres semaines qui l'utilisent."
              : "Tant que le mapping n'est pas modifié, le mapping enregistré est réutilisé.")}
            {editing && " Une feuille dont « Traiter cette feuille » est désactivé est retirée du chargement."}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {isLoading && (
            <div className="flex items-center justify-center py-10">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
              <p className="ml-3 text-gray-600 dark:text-gray-300">Lecture du fichier d'origine...</p>
            </div>
          )}
          {error && <p className="py-6 text-center text-red-500 dark:text-red-400">Impossible de lire le fichier : {error.message}</p>}
          {!isLoading && !error && preview && (
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="flex shrink-0 flex-col gap-1 md:w-52">
                {sheets.map((s) => {
                  const status = editing ? (draft[s.sheet]?.enabled ? 'enabled' : 'disabled') : sheetStatus(s);
                  return (
                    <button
                      key={s.sheet}
                      type="button"
                      onClick={() => setSelected(s.sheet)}
                      className={`flex items-center justify-between rounded px-3 py-2 text-left text-sm ${
                        s.sheet === selected
                          ? 'bg-blue-50 text-blue-800 ring-1 ring-blue-400 dark:bg-blue-900/30 dark:text-blue-200'
                          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                      }`}
                    >
                      <span className="truncate" title={s.sheet}>{s.sheet}</span>
                      <Badge status={status} />
                    </button>
                  );
                })}
              </div>
              <div className="min-w-0 flex-1">
                {editing
                  ? <MappingEditor preview={preview} load={load} draft={draft} choices={choices} onChange={(sheetDraft) => setDraft((d) => ({ ...d, [preview.sheet]: sheetDraft }))} />
                  : (preview.loaded ? <MappingView preview={preview} load={load} /> : <RawView preview={preview} />)}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 border-t border-gray-200 bg-gray-50 px-5 py-4 dark:border-gray-700 dark:bg-gray-700">
          {!editing && brokenSheets.length > 0 && (
            <p className="text-sm text-red-600 dark:text-red-300">
              Le mapping ne correspond plus au fichier pour : {brokenSheets.map((s) => s.sheet).join(', ')}. Corrigez-le avec « Modifier le mapping ».
            </p>
          )}
          {editing && errors.length > 0 && (
            <ul className="list-disc pl-5 text-sm text-red-600 dark:text-red-300">
              {errors.map((e) => <li key={e}>{e}</li>)}
            </ul>
          )}
          {dropped.length > 0 && (
            <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
              Ces feuilles sont chargées aujourd'hui et ne le seront plus : leurs données seront supprimées.{' '}
              {dropped.map((s) => (s.loaded_rows != null
                ? `${s.sheet} (${s.loaded_rows.toLocaleString('fr-FR')} lignes)`
                : s.sheet)).join(', ')}
            </p>
          )}
          {editing && errors.length === 0 && !changed && (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Aucune modification : le mapping enregistré sera réutilisé, sans en créer un nouveau.
            </p>
          )}
          {/* The mode toggle sits on the left, next to Fermer: on the right, a double-click on it
              would land on the confirm button that replaces it and start the reprocess */}
          <div className="flex flex-row-reverse flex-wrap gap-2">
            {editing ? (
              <button
                type="button"
                disabled={errors.length > 0}
                onClick={() => onConfirm(changed ? buildSheetsOverride(draft, sheets) : null)}
                className="rounded-md bg-yellow-600 px-4 py-2 text-white hover:bg-yellow-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-yellow-500 dark:hover:bg-yellow-600"
              >
                Retraiter avec ce mapping
              </button>
            ) : (
              <button
                type="button"
                disabled={isLoading || Boolean(error) || brokenSheets.length > 0}
                onClick={() => onConfirm(null)}
                className="rounded-md bg-yellow-600 px-4 py-2 text-white hover:bg-yellow-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-yellow-500 dark:hover:bg-yellow-600"
              >
                Retraiter
              </button>
            )}
            {editing ? (
              <button type="button" onClick={() => { setEditing(false); setDraft(initialDraft(sheets)); }} className="mr-auto rounded-md border border-gray-300 bg-white px-4 py-2 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300">
                Annuler les modifications
              </button>
            ) : (
              <button type="button" disabled={isLoading || Boolean(error)} onClick={() => setEditing(true)} className="mr-auto rounded-md border border-blue-500 bg-white px-4 py-2 text-blue-600 hover:bg-blue-50 disabled:opacity-50 dark:bg-gray-800 dark:text-blue-400">
                Modifier le mapping
              </button>
            )}
            <button type="button" onClick={onCancel} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300">
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReprocessDialog;
